import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath } from 'react-router';

import { useAnnouncementsApiClient } from '@/api/clients/useAnnouncementsApiClient.util.ts';
import { PermissionDto } from '@/api/generated';
import { AnnouncementsContextProvider } from '@/contexts/announcements/Announcements.context.tsx';
import {
  announcementDefinitions,
  emailAnnouncementAnchorKeys,
} from '@/contexts/announcements/announcements.definitions.ts';
import { AnnouncementsCoachmark } from '@/contexts/announcements/components/AnnouncementsCoachmark.comp.tsx';
import { AnnouncementsPanel } from '@/contexts/announcements/components/AnnouncementsPanel.comp.tsx';
import {
  AnnouncementAnchorRegistry,
  AnnouncementSeenMap,
  AnnouncementSession,
  ResolvedAnnouncement,
} from '@/contexts/announcements/model/Announcements.model.ts';
import { useAppConfig } from '@/contexts/appConfig/AppConfig.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useHasPermission } from '@/contexts/userContext/util/useHasPermission.util.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useAppNavigation } from '@/utils/useAppNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

const ALL_ANNOUNCEMENT_IDS = announcementDefinitions.map(({ announcementId }) => announcementId);

export const AnnouncementsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { isEmailEnabled } = useAppConfig();
  const { dataReady, role, userId } = useUserContext();
  const canReadAnnouncements = useHasPermission(PermissionDto.AnnouncementRead);
  const { location } = useAppNavigation();
  const { t } = useTranslationWithPrefix('components.announcements');
  const { announcementsSeen, isGettingAnnouncementsSeen, markAnnouncementsSeen } =
    useAnnouncementsApiClient({
      announcementIds: ALL_ANNOUNCEMENT_IDS,
      enabled: dataReady && !!userId && canReadAnnouncements,
      userId,
    });
  const anchorRegistryRef = useRef<AnnouncementAnchorRegistry>(new Map());
  const autoOpenedSignatureRef = useRef<string | null>(null);
  const [, setRegistryVersion] = useState(0);
  const [isSeenMapHydrated, setIsSeenMapHydrated] = useState(false);
  const [seenMap, setSeenMap] = useState<AnnouncementSeenMap>({});
  const [activeSession, setActiveSession] = useState<AnnouncementSession | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const closeActiveSession = useCallback(() => {
    setActiveSession(null);
  }, []);

  const setActiveSessionIndex = useCallback((currentIndex: number) => {
    setActiveSession(current => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        currentIndex,
      };
    });
  }, []);

  const openAnnouncementSession = useCallback(
    (announcementIds: string[], mode: AnnouncementSession['mode']) => {
      setActiveSession({
        announcementIds,
        currentIndex: 0,
        mode,
      });
    },
    [],
  );

  useEffect(() => {
    if (!dataReady || !userId) {
      setSeenMap({});
      setIsSeenMapHydrated(false);
      return;
    }

    if (!announcementsSeen) {
      return;
    }

    setSeenMap(
      announcementsSeen.items.reduce<AnnouncementSeenMap>((acc, item) => {
        acc[item.announcementId] = item.seen;
        return acc;
      }, {}),
    );
    setIsSeenMapHydrated(true);
  }, [announcementsSeen, dataReady, userId]);

  const currentRouteAnnouncements: ResolvedAnnouncement[] = announcementDefinitions
    .filter(definition => {
      if (!isEmailEnabled && emailAnnouncementAnchorKeys.has(definition.anchorKey)) {
        return false;
      }

      if (!canReadAnnouncements) {
        return false;
      }

      if (definition.requiresRole && !role) {
        return false;
      }

      return matchesAnnouncementRoute(definition.route, location.pathname);
    })
    .sort((left, right) => left.order - right.order)
    .map(definition => {
      const anchorElement = anchorRegistryRef.current.get(definition.anchorKey);

      return {
        ...definition,
        description: t(`${definition.translationKey}.description`),
        isAvailable: isAnchorAvailable(anchorElement),
        isSeen: seenMap[definition.announcementId],
        title: t(`${definition.translationKey}.title`),
      };
    });

  const currentRouteAnnouncementMap = useMemo(() => {
    return new Map(
      currentRouteAnnouncements.map(announcement => [announcement.announcementId, announcement]),
    );
  }, [currentRouteAnnouncements]);

  const sessionAnnouncements = useMemo(() => {
    if (!activeSession) {
      return [];
    }

    return activeSession.announcementIds
      .map(announcementId => currentRouteAnnouncementMap.get(announcementId))
      .filter(
        (announcement): announcement is ResolvedAnnouncement =>
          !!announcement && announcement.isAvailable,
      );
  }, [activeSession, currentRouteAnnouncementMap]);

  const activeAnnouncement =
    activeSession && sessionAnnouncements.length > 0
      ? sessionAnnouncements[Math.min(activeSession.currentIndex, sessionAnnouncements.length - 1)]
      : null;

  const currentRouteUnseenCount = currentRouteAnnouncements.filter(item => !item.isSeen).length;

  useEffect(() => {
    if (!activeSession) {
      return;
    }

    if (sessionAnnouncements.length === 0) {
      closeActiveSession();
      return;
    }

    if (activeSession.currentIndex > sessionAnnouncements.length - 1) {
      setActiveSessionIndex(sessionAnnouncements.length - 1);
    }
  }, [activeSession, closeActiveSession, sessionAnnouncements.length, setActiveSessionIndex]);

  useEffect(() => {
    if (
      isGettingAnnouncementsSeen ||
      !isSeenMapHydrated ||
      activeSession ||
      isPanelOpen ||
      currentRouteAnnouncements.length === 0
    ) {
      return;
    }

    const unseenAnnouncements = currentRouteAnnouncements.filter(
      announcement => announcement.isAvailable && !announcement.isSeen,
    );

    if (unseenAnnouncements.length === 0) {
      return;
    }

    const signature = `${location.pathname}:${unseenAnnouncements
      .map(announcement => announcement.announcementId)
      .join('|')}`;

    if (autoOpenedSignatureRef.current === signature) {
      return;
    }

    autoOpenedSignatureRef.current = signature;
    openAnnouncementSession(
      unseenAnnouncements.map(announcement => announcement.announcementId),
      'auto',
    );
  }, [
    activeSession,
    currentRouteAnnouncements,
    isSeenMapHydrated,
    isGettingAnnouncementsSeen,
    isPanelOpen,
    location.pathname,
    openAnnouncementSession,
  ]);

  const registerAnchor = useCallback((anchorKey: string, element: HTMLElement) => {
    if (anchorRegistryRef.current.get(anchorKey) === element) {
      return;
    }

    anchorRegistryRef.current.set(anchorKey, element);
    setRegistryVersion(current => current + 1);
  }, []);

  const unregisterAnchor = useCallback((anchorKey: string, element?: HTMLElement) => {
    const currentElement = anchorRegistryRef.current.get(anchorKey);

    if (!currentElement) {
      return;
    }

    if (element && currentElement !== element) {
      return;
    }

    anchorRegistryRef.current.delete(anchorKey);
    setRegistryVersion(current => current + 1);
  }, []);

  const markAnnouncementsAsSeen = useCallback(
    (announcementIds: string[]) => {
      const uniqueIds = Array.from(new Set(announcementIds));

      if (uniqueIds.length === 0) {
        return;
      }

      setSeenMap(current => {
        return uniqueIds.reduce<AnnouncementSeenMap>(
          (acc, announcementId) => {
            acc[announcementId] = true;
            return acc;
          },
          { ...current },
        );
      });

      if (!userId) {
        return;
      }

      void markAnnouncementsSeen({
        announcementIds: uniqueIds,
        userId,
      }).catch(() => null);
    },
    [markAnnouncementsSeen, userId],
  );

  const handleCloseCoachmark = useCallback(() => {
    if (!activeSession || sessionAnnouncements.length === 0) {
      closeActiveSession();
      return;
    }

    const activeAnnouncementId =
      sessionAnnouncements[Math.min(activeSession.currentIndex, sessionAnnouncements.length - 1)]
        .announcementId;

    markAnnouncementsAsSeen([activeAnnouncementId]);

    if (activeSession.currentIndex >= sessionAnnouncements.length - 1) {
      closeActiveSession();
      return;
    }

    setActiveSessionIndex(activeSession.currentIndex + 1);
  }, [
    activeSession,
    closeActiveSession,
    markAnnouncementsAsSeen,
    sessionAnnouncements,
    setActiveSessionIndex,
  ]);

  const handleSkipCoachmark = useCallback(() => {
    if (!activeSession) {
      closeActiveSession();
      return;
    }

    markAnnouncementsAsSeen(activeSession.announcementIds);
    closeActiveSession();
  }, [activeSession, closeActiveSession, markAnnouncementsAsSeen]);

  const previewAnnouncement = useCallback(
    (announcementId: string) => {
      const announcement = currentRouteAnnouncementMap.get(announcementId);

      if (!announcement?.isAvailable) {
        return;
      }

      setIsPanelOpen(false);
      openAnnouncementSession([announcementId], 'manual');
    },
    [currentRouteAnnouncementMap, openAnnouncementSession],
  );

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const contextValue = useMemo(() => {
    return {
      canReadAnnouncements,
      closePanel,
      currentRouteAnnouncements,
      currentRouteUnseenCount,
      isPanelOpen,
      openPanel,
      previewAnnouncement,
      registerAnchor,
      unregisterAnchor,
    };
  }, [
    canReadAnnouncements,
    closePanel,
    currentRouteAnnouncements,
    currentRouteUnseenCount,
    isPanelOpen,
    openPanel,
    previewAnnouncement,
    registerAnchor,
    unregisterAnchor,
  ]);

  return (
    <AnnouncementsContextProvider value={contextValue}>
      {children}
      <AnnouncementsPanel
        announcements={currentRouteAnnouncements}
        isOpen={isPanelOpen}
        onClose={closePanel}
        onPreview={previewAnnouncement}
      />
      {activeAnnouncement && (
        <AnnouncementsCoachmark
          anchorElement={
            anchorRegistryRef.current.get(activeAnnouncement.anchorKey) ?? document.body
          }
          announcement={activeAnnouncement}
          currentIndex={activeSession?.currentIndex ?? 0}
          onClose={handleCloseCoachmark}
          onSkip={handleSkipCoachmark}
          total={sessionAnnouncements.length}
        />
      )}
    </AnnouncementsContextProvider>
  );
};

const isAnchorAvailable = (anchorElement?: HTMLElement) => {
  if (!anchorElement) {
    return false;
  }

  const rect = anchorElement.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
};

const matchesAnnouncementRoute = (route: RouteEnum | RouteEnum[], pathname: string) => {
  const routes = Array.isArray(route) ? route : [route];

  return routes.some(item => {
    if (matchPath({ end: true, path: item }, pathname)) {
      return true;
    }

    if (item === RouteEnum.ROOT || item.includes(':')) {
      return false;
    }

    return !!matchPath({ end: false, path: `${item}/*` }, pathname);
  });
};
